import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Check,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  BookOpen,
  AlertTriangle,
  RefreshCw,
  Download,
} from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import {
  DELIVERY_PIPELINE_STEPS,
  MESSAGING_API_STAGES,
  SCRIPT_PIPELINE_PHASES,
  TEMPLATE_VARIABLES,
  MESSAGING_EVENTS,
  eventLabel,
  methodBadgeClass,
  type ApiEndpointDoc,
  type MessagingEventKey,
} from '../../lib/messagingApiCatalog';
import { EXTERNAL_TEMPLATE_CHANNELS } from '../../lib/messagingConstants';
import { downloadMessagingPostmanCollection } from '../../lib/messagingPostmanCollection';
import { getPublicApiBaseUrl } from '../../lib/publicApiBase';

type WabaConnection = {
  id: string;
  organizationId: string;
  organization?: { id: string; name: string; type: string };
  wabaId: string;
  phoneNumberId: string;
  status: string;
  lastValidatedAt?: string | null;
};

type TemplateRow = {
  id: string;
  organizationId: string;
  eventKey: string;
  channel: string;
  subject?: string | null;
  body: string;
  isActive: boolean;
};

type VariantRow = {
  id: string;
  name: string;
  eventKey: string;
  channel: string;
  subject?: string | null;
  body: string;
};

type BuiltInDefaults = Record<
  string,
  Partial<Record<'WHATSAPP' | 'EMAIL' | 'SMS', { subject?: string; body: string }>>
>;

type OutboxRow = {
  id: string;
  channel: string;
  status: string;
  messagingEventKey?: string | null;
  destination: string;
  attempts: number;
  lastError?: string | null;
  createdAt: string;
  organization?: { id: string; name: string } | null;
};

function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }, [value]);

  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-accent2 hover:bg-glass-3"
      title={`Copy ${label}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function JsonBlock({ data }: { data: unknown }) {
  return (
    <pre className="mt-2 overflow-x-auto rounded-lg border border-glass bg-glass-2/60 p-3 text-xs font-mono text-glass-text-2 whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function EndpointCard({
  endpoint,
  liveTemplateBody,
}: {
  endpoint: ApiEndpointDoc;
  liveTemplateBody?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const hasDetails = !!(
    endpoint.sampleRequest ||
    endpoint.sampleResponse ||
    endpoint.notes ||
    liveTemplateBody
  );

  return (
    <div className="rounded-xl border border-glass bg-glass-1/60 overflow-hidden">
      <div className="flex flex-wrap items-start gap-2 p-4">
        <span
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[0.65rem] font-bold tracking-wide ${methodBadgeClass(
            endpoint.method
          )}`}
        >
          {endpoint.method}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-sm font-mono text-glass-text break-all">{endpoint.path}</code>
            <CopyButton value={endpoint.path} label="Path" />
          </div>
          <p className="mt-1 text-sm text-glass-text-2">{endpoint.purpose}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {endpoint.actor ? (
              <span className="rounded-full bg-glass-3 px-2 py-0.5 text-[0.65rem] text-glass-text-2">
                Actor: {endpoint.actor}
              </span>
            ) : null}
            {endpoint.eventKeys?.map((k) => (
              <span
                key={k}
                className="rounded-full bg-accent2/10 border border-accent2/25 px-2 py-0.5 text-[0.65rem] text-accent2"
                title={eventLabel(k)}
              >
                {k}
              </span>
            ))}
            {endpoint.channels?.map((c) => (
              <span
                key={c}
                className="rounded-full border border-glass bg-glass-2 px-2 py-0.5 text-[0.65rem] text-glass-text-2"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
        {hasDetails ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-glass-text-2 hover:bg-glass-3"
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Details
          </button>
        ) : null}
      </div>
      {open && hasDetails ? (
        <div className="border-t border-glass px-4 py-3 space-y-3 bg-glass-2/30">
          {endpoint.notes ? (
            <p className="text-xs text-glass-text-2 leading-relaxed">{endpoint.notes}</p>
          ) : null}
          {liveTemplateBody ? (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-glass-text-2 mb-1">
                Live org template body (first linked event)
              </p>
              <pre className="rounded-lg border border-glass bg-glass-1 p-3 text-xs font-mono text-glass-text whitespace-pre-wrap">
                {liveTemplateBody}
              </pre>
            </div>
          ) : null}
          {endpoint.sampleRequest ? (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-glass-text-2">
                Sample request
              </p>
              <JsonBlock data={endpoint.sampleRequest} />
            </div>
          ) : null}
          {endpoint.sampleResponse ? (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-glass-text-2">
                Sample response
              </p>
              <JsonBlock data={endpoint.sampleResponse} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function formatTs(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AgencyApiDocs() {
  const { user } = useAuthStore();
  const isOrgAdmin = ['OWNER', 'ADMIN'].includes(user?.roleInOrg || '');

  const agencyOrgs = useMemo(() => {
    const memberships = user?.memberships?.filter((m) => m.organization?.type === 'AGENCY') || [];
    return memberships.map((m) => ({
      id: m.organizationId,
      name: m.organization?.name || m.organizationId,
    }));
  }, [user?.memberships]);

  const orgId = user?.organizationId || agencyOrgs[0]?.id || '';

  const [waba, setWaba] = useState<WabaConnection[]>([]);
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [builtIn, setBuiltIn] = useState<BuiltInDefaults | null>(null);
  const [outbox, setOutbox] = useState<OutboxRow[]>([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState('');

  const loadLive = useCallback(async () => {
    if (!orgId) return;
    setLiveLoading(true);
    setLiveError('');
    try {
      const [wabaRes, tplRes, builtInRes, outboxRes] = await Promise.all([
        api.get<WabaConnection[]>('/waba/connections'),
        api.get<{ templates: TemplateRow[]; variants: VariantRow[] }>('/messaging-templates', {
          params: { organizationId: orgId, includeVariants: '1' },
        }),
        api.get<BuiltInDefaults>('/messaging-templates/built-in-defaults'),
        api.get<OutboxRow[]>('/notification-outbox', {
          params: { limit: 10, organizationId: orgId },
        }),
      ]);
      setWaba(wabaRes.data || []);
      setTemplates(tplRes.data.templates || []);
      setVariants(tplRes.data.variants || []);
      setBuiltIn(builtInRes.data || null);
      setOutbox(outboxRes.data || []);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to load live messaging data';
      setLiveError(msg);
    } finally {
      setLiveLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (isOrgAdmin && orgId) {
      loadLive();
    }
  }, [isOrgAdmin, orgId, loadLive]);

  const orgWaba = useMemo(
    () => waba.filter((c) => c.organizationId === orgId || !orgId),
    [waba, orgId]
  );

  const templateLookup = useMemo(() => {
    const map = new Map<string, TemplateRow>();
    for (const t of templates) {
      map.set(`${t.eventKey}::${t.channel}`, t);
    }
    return map;
  }, [templates]);

  function coverageFor(eventKey: string, channel: string): {
    status: 'active' | 'inactive' | 'built_in' | 'missing';
    body?: string;
  } {
    const row = templateLookup.get(`${eventKey}::${channel}`);
    if (row) {
      return {
        status: row.isActive ? 'active' : 'inactive',
        body: row.body,
      };
    }
    const def = builtIn?.[eventKey]?.[channel as 'WHATSAPP' | 'EMAIL' | 'SMS'];
    if (def?.body) {
      return { status: 'built_in', body: def.body };
    }
    return { status: 'missing' };
  }

  function liveBodyForEndpoint(endpoint: ApiEndpointDoc): string | null {
    const key = endpoint.eventKeys?.[0];
    if (!key) return null;
    // Prefer WhatsApp, then EMAIL
    for (const ch of ['WHATSAPP', 'EMAIL'] as const) {
      const cov = coverageFor(key, ch);
      if (cov.body) return `[${ch}] ${cov.body}`;
    }
    return null;
  }

  if (!isOrgAdmin) {
    return <Navigate to="/agency/dashboard" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[2rem] font-semibold tracking-tight text-glass-text flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-accent2" />
            Messaging API docs
          </h1>
          <p className="mt-2 text-sm text-glass-text-2 max-w-3xl leading-relaxed">
            Reference for every endpoint agency staff use to message KOLs as a campaign progresses —
            invitation → script → submission → payment, plus manual WhatsApp and template tooling.
            Restricted to organization <strong className="text-glass-text">OWNER</strong> and{' '}
            <strong className="text-glass-text">ADMIN</strong>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              downloadMessagingPostmanCollection({
                baseUrl: getPublicApiBaseUrl() || 'https://api.kol.my',
              })
            }
            title="Download Postman Collection v2.1 for all endpoints listed on this page"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Download Postman file
          </Button>
          <Link to="/agency/whatsapp-mock">
            <Button size="sm" variant="outline">
              WhatsApp mock lab
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={loadLive} disabled={liveLoading || !orgId}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${liveLoading ? 'animate-spin' : ''}`} />
            Refresh live data
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 items-start">
        {/* TOC */}
        <aside className="lg:sticky lg:top-4 space-y-1 rounded-xl border border-glass bg-glass-1/50 p-3">
          <p className="px-2 pb-2 text-[0.65rem] font-semibold uppercase tracking-wide text-glass-text-2">
            On this page
          </p>
          <a
            href="#live"
            className="block rounded-lg px-2 py-1.5 text-sm text-glass-text-2 hover:bg-glass-3 hover:text-glass-text"
          >
            Live status
          </a>
          <a
            href="#pipeline"
            className="block rounded-lg px-2 py-1.5 text-sm text-glass-text-2 hover:bg-glass-3 hover:text-glass-text"
          >
            Delivery pipeline
          </a>
          {MESSAGING_API_STAGES.map((stage) => (
            <a
              key={stage.id}
              href={`#${stage.id}`}
              className="block rounded-lg px-2 py-1.5 text-sm text-glass-text-2 hover:bg-glass-3 hover:text-glass-text"
            >
              {stage.title}
            </a>
          ))}
          <a
            href="#variables"
            className="block rounded-lg px-2 py-1.5 text-sm text-glass-text-2 hover:bg-glass-3 hover:text-glass-text"
          >
            Template variables
          </a>
          <a
            href="#events"
            className="block rounded-lg px-2 py-1.5 text-sm text-glass-text-2 hover:bg-glass-3 hover:text-glass-text"
          >
            Event keys
          </a>
        </aside>

        <div className="space-y-8 min-w-0">
          {/* Live panels */}
          <section id="live" className="scroll-mt-6 space-y-4">
            <h2 className="text-xl font-semibold text-glass-text">Live status</h2>
            {liveError ? (
              <p className="text-sm text-rose-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {liveError}
              </p>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">WABA connection</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {liveLoading && !orgWaba.length ? (
                    <p className="text-glass-text-2">Loading…</p>
                  ) : orgWaba.length === 0 ? (
                    <p className="text-amber-400/90">Not connected</p>
                  ) : (
                    orgWaba.map((c) => (
                      <div key={c.id} className="rounded-lg border border-glass bg-glass-2/40 p-2">
                        <p className="font-medium text-glass-text">
                          {c.organization?.name || c.organizationId}
                        </p>
                        <p className="text-xs text-glass-text-2">
                          Status: {c.status} · Phone ID: {c.phoneNumberId}
                        </p>
                      </div>
                    ))
                  )}
                  <Link
                    to="/agency/messaging"
                    className="inline-flex items-center gap-1 text-xs text-accent2 hover:underline"
                  >
                    Open WhatsApp & messaging <ExternalLink className="w-3 h-3" />
                  </Link>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
                  <CardTitle className="text-base">Template coverage</CardTitle>
                  <Link
                    to="/agency/messaging-templates"
                    className="inline-flex items-center gap-1 text-xs text-accent2 hover:underline"
                  >
                    Edit templates <ExternalLink className="w-3 h-3" />
                  </Link>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-glass-text-2 border-b border-glass">
                          <th className="py-2 pr-2 font-medium">Event</th>
                          {EXTERNAL_TEMPLATE_CHANNELS.map((ch) => (
                            <th key={ch} className="py-2 px-1 font-medium">
                              {ch}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {MESSAGING_EVENTS.map((ev) => (
                          <tr key={ev.key} className="border-b border-glass/60">
                            <td className="py-1.5 pr-2 text-glass-text font-mono">{ev.key}</td>
                            {EXTERNAL_TEMPLATE_CHANNELS.map((ch) => {
                              const cov = coverageFor(ev.key, ch);
                              const color =
                                cov.status === 'active'
                                  ? 'text-emerald-400'
                                  : cov.status === 'built_in'
                                    ? 'text-sky-400'
                                    : cov.status === 'inactive'
                                      ? 'text-amber-400'
                                      : 'text-rose-400/80';
                              const label =
                                cov.status === 'active'
                                  ? 'Org'
                                  : cov.status === 'built_in'
                                    ? 'Default'
                                    : cov.status === 'inactive'
                                      ? 'Off'
                                      : '—';
                              return (
                                <td key={ch} className={`py-1.5 px-1 ${color}`} title={cov.body || ''}>
                                  {label}
                                  {variants.some((v) => v.eventKey === ev.key && v.channel === ch)
                                    ? ' +var'
                                    : ''}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-[0.65rem] text-glass-text-2">
                    Org = active org template · Default = built-in fallback · Off = inactive org row · +var =
                    named variants exist
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">Recent deliveries</CardTitle>
                <Link
                  to="/agency/messaging-log"
                  className="inline-flex items-center gap-1 text-xs text-accent2 hover:underline"
                >
                  Full messaging log <ExternalLink className="w-3 h-3" />
                </Link>
              </CardHeader>
              <CardContent>
                {outbox.length === 0 ? (
                  <p className="text-sm text-glass-text-2">
                    {liveLoading ? 'Loading…' : 'No recent outbox rows for this organization.'}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-glass-text-2 border-b border-glass">
                          <th className="py-2 pr-2">When</th>
                          <th className="py-2 pr-2">Status</th>
                          <th className="py-2 pr-2">Channel</th>
                          <th className="py-2 pr-2">Event</th>
                          <th className="py-2 pr-2">Destination</th>
                          <th className="py-2">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {outbox.map((row) => (
                          <tr key={row.id} className="border-b border-glass/50">
                            <td className="py-1.5 pr-2 text-glass-text-2 whitespace-nowrap">
                              {formatTs(row.createdAt)}
                            </td>
                            <td className="py-1.5 pr-2 font-medium text-glass-text">{row.status}</td>
                            <td className="py-1.5 pr-2">{row.channel}</td>
                            <td className="py-1.5 pr-2 font-mono">
                              {row.messagingEventKey || '—'}
                            </td>
                            <td className="py-1.5 pr-2 max-w-[140px] truncate" title={row.destination}>
                              {row.destination}
                            </td>
                            <td
                              className="py-1.5 max-w-[200px] truncate text-rose-400/90"
                              title={row.lastError || ''}
                            >
                              {row.lastError || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Pipeline */}
          <section id="pipeline" className="scroll-mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Delivery pipeline</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-2">
                  {DELIVERY_PIPELINE_STEPS.map((step, i) => (
                    <li key={step} className="flex gap-3 text-sm text-glass-text-2">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent2/15 text-accent2 text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 text-xs text-glass-text-2 leading-relaxed">
                  Script pipeline phases on invitations:{' '}
                  <code className="text-glass-text">{SCRIPT_PIPELINE_PHASES.join(' → ')}</code>
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Stages */}
          {MESSAGING_API_STAGES.map((stage) => (
            <section key={stage.id} id={stage.id} className="scroll-mt-6 space-y-3">
              <div>
                <h2 className="text-xl font-semibold text-glass-text">{stage.title}</h2>
                <p className="mt-1 text-sm text-glass-text-2 leading-relaxed">{stage.summary}</p>
                {stage.statuses?.length ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {stage.statuses.map((s) => (
                      <span
                        key={s}
                        className="rounded-md border border-glass bg-glass-2 px-2 py-0.5 text-[0.65rem] font-mono text-glass-text-2"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                ) : null}
                {stage.id === 'payment' ? (
                  <p className="mt-2 text-xs text-amber-400/90 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    Gap for WhatsApp provider integration: no MessagingEventKey for payment notices yet.
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                {stage.endpoints.map((ep) => (
                  <EndpointCard
                    key={ep.id}
                    endpoint={ep}
                    liveTemplateBody={liveBodyForEndpoint(ep)}
                  />
                ))}
              </div>
            </section>
          ))}

          {/* Variables */}
          <section id="variables" className="scroll-mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Template variables</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-glass-text-2 mb-3">
                  Placeholders use <code className="text-glass-text">{'{{variableName}}'}</code> syntax
                  and are substituted when the outbox renders a template.
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {TEMPLATE_VARIABLES.map((v) => (
                    <li
                      key={v}
                      className="rounded-lg border border-glass bg-glass-2/40 px-3 py-2 text-xs font-mono text-glass-text flex items-center justify-between gap-2"
                    >
                      <span>{v}</span>
                      <CopyButton value={v.split(' — ')[0]} />
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Events */}
          <section id="events" className="scroll-mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Messaging event keys</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {MESSAGING_EVENTS.map((ev) => (
                    <li
                      key={ev.key}
                      className="flex flex-wrap items-baseline gap-2 text-sm border-b border-glass/50 pb-2"
                    >
                      <code className="font-mono text-accent2">{ev.key as MessagingEventKey}</code>
                      <span className="text-glass-text-2">{ev.label}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
}
